import { IInlineSearchActions } from '@/interfaces/actions.interface';
import store from '@/store';
import TabsKind from '@/enums/tabs.enum';
import { DependencyCheckerPlugin } from '@/store/plugins/DependencyCheckerPlugin';
import globals from '@/utils/global';
import { FilterKey } from '@studyportals/search-filters/server-side';
import { disciplinesAll } from '@studyportals/static-domain-data/disciplines/disciplines';
import { FacetValues, ProgrammeSearchInput } from '@studyportals/search-api-interface';
import { DataStorageSyncPlugin } from '@/store/plugins/DataStorageSyncPlugin';
import { countryGetIsoCode  } from '@studyportals/static-domain-data/countries/country-get-iso-code';
import DOMPlugin from '@/store/plugins/DOMPlugin';
import { SuggestionResult } from '@/interfaces/suggestions-result.interface';
const actions: IInlineSearchActions = {
	init: (): void => {
		new DOMPlugin().initialize();
		void new DependencyCheckerPlugin().initialize();
		void new DataStorageSyncPlugin().initiate();

	},
	updateProgrammesResults: async (text) => {
		if (!store.getters.amIVisible()) return;

		store.mutations.setSelectedTab(TabsKind.SEARCHING_FOR);

		const hasProgrammeSearchTextValue = store.getters.getProgrammeSearchInput()?.text;
		const hasAnyText = store.getters.getText();

		if (text) {
			store.mutations.setText(text);
		}

		let withLocalTextSearchInput = {} as ProgrammeSearchInput;

		if (!hasAnyText && hasProgrammeSearchTextValue) {
			withLocalTextSearchInput = {
				text: store.getters.getText(),
				...store.getters.getProgrammeSearchInput()!,
			};
		} else {
			withLocalTextSearchInput = {
				...store.getters.getProgrammeSearchInput()!,
				text: store.getters.getText()
			};
		}

		withLocalTextSearchInput.nationalityCountryIso = countryGetIsoCode(store.getters.getUserNationality().toString())!;

		store.mutations.setText(withLocalTextSearchInput.text!);

		if (!withLocalTextSearchInput.text
			|| withLocalTextSearchInput.text.length < globals.MIN_SEARCH_LENGTH) {
			resetResults(TabsKind.SEARCHING_FOR);
			return;
		}

		const results = await globals.resultsRepository.getResult(withLocalTextSearchInput);

		const anyResults = results && results.hitsTotal > 0;
		if (!anyResults) {
			resetResults(TabsKind.PROGRAMMES);
			return;
		}

		store.mutations.setResults(TabsKind.PROGRAMMES, results);
		store.mutations.setTabResultsAmount(TabsKind.PROGRAMMES, results.hitsTotal);
		store.mutations.setSelectedTab(TabsKind.PROGRAMMES);
	},
	loadDisciplineFacets: async () => {
		const facets = await globals.disciplineFacetsRepository.getDisciplineFacets();

		if (!facets.length) return;

		const disciplinesIds: string[] = disciplinesAll.map(d => d.id);
		const sortedFacets: FacetValues[] = facets.sort((a, b) => disciplinesIds.indexOf(a.value) - disciplinesIds.indexOf(b.value)
		);

		store.mutations.setFacets({ [FilterKey.DISCIPLINES]: sortedFacets });
	},
	setDisciplineAsFilter: async (discipline: FacetValues): Promise<void> => {
		const filterProvider = store.getters.getFilterProvider();
		if (!filterProvider) return;
		await filterProvider.processFilterSelection({ key: FilterKey.DISCIPLINES, value: discipline.value });
	},
	updateSuggestions: async (text: string): Promise<void> => {
		const hasProgrammeSearchTextValue = store.getters.getProgrammeSearchInput()?.text;
		const hasAnyText = store.getters.getText();

		if (!text) {
			store.mutations.setSuggestions([]);
			store.mutations.setText('');
			return;
		}

		store.mutations.setText(text);

		let withLocalTextSearchInput = {} as ProgrammeSearchInput;

		if (!hasAnyText && hasProgrammeSearchTextValue) {
			withLocalTextSearchInput = {
				text,
				...store.getters.getProgrammeSearchInput()!,
			};
		} else {
			withLocalTextSearchInput = {
				...store.getters.getProgrammeSearchInput()!,
				text: store.getters.getText(),
			};
		}

		const suggestions = await globals.suggestionsRepository.getSuggestions(
			withLocalTextSearchInput
		);

		if (!suggestions || !suggestions.length) {
			store.mutations.setSuggestions([]);
			return;
		}

		const mappedSuggestions =  suggestions?.map((suggestion: SuggestionResult) => {
			return ({
				originalText: text.toLowerCase().trim(),
				fullText: suggestion.suggestion,
				programmesCount: suggestion.amount_of_programmes,
			})
		});

		if(mappedSuggestions){
			store.mutations.setSuggestions(mappedSuggestions);
			return;
		}

		store.mutations.setSuggestions([{
			originalText: text.toLowerCase().trim(),
			fullText: `<match>${text}</match>`,
			programmesCount: 0,
		}]);
	}
};

const resetResults = (tab: TabsKind): void => {
	store.mutations.setResults(tab, null);
	store.mutations.setTabResultsAmount(tab, 0);
}

export default actions;